import { format, formatDistanceToNow, isToday, isYesterday, isThisWeek, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

/**
 * Format a date for display in the chat
 * @param {string|Date} date - The date to format
 * @param {Object} [options] - Formatting options
 * @param {boolean} [options.relative=true] - Whether to use relative time (e.g., "2 hours ago")
 * @param {boolean} [options.withTime=true] - Whether to include the time in the output
 * @returns {string} Formatted date string
 */
export const formatChatDate = (date, { relative = true, withTime = true } = {}) => {
  if (!date) return '';
  
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const now = new Date();
  
  if (relative) {
    // For recent messages, show relative time
    const diffInMinutes = (now - dateObj) / (1000 * 60);
    
    if (diffInMinutes < 1) {
      return 'À l\'instant';
    }
    
    if (diffInMinutes < 60) {
      const minutes = Math.floor(diffInMinutes);
      return `Il y a ${minutes} minute${minutes > 1 ? 's' : ''}`;
    }
    
    if (diffInMinutes < 60 * 24) {
      const hours = Math.floor(diffInMinutes / 60);
      return `Il y a ${hours} heure${hours > 1 ? 's' : ''}`;
    }
    
    // For older messages, show date with time
    return format(dateObj, withTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy', { locale: fr });
  }
  
  // For absolute dates
  if (isToday(dateObj)) {
    return withTime 
      ? `Aujourd'hui, ${format(dateObj, 'HH:mm', { locale: fr })}`
      : 'Aujourd\'hui';
  }
  
  if (isYesterday(dateObj)) {
    return withTime
      ? `Hier, ${format(dateObj, 'HH:mm', { locale: fr })}`
      : 'Hier';
  }
  
  if (isThisWeek(dateObj, { weekStartsOn: 1 })) {
    return withTime
      ? `${format(dateObj, 'EEEE', { locale: fr })}, ${format(dateObj, 'HH:mm', { locale: fr })}`
      : format(dateObj, 'EEEE', { locale: fr });
  }
  
  return format(dateObj, withTime ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy', { locale: fr });
};

/**
 * Group messages by date for display
 * @param {Array} messages - Array of message objects
 * @returns {Object} Messages grouped by date
 */
export const groupMessagesByDate = (messages = []) => {
  if (!Array.isArray(messages)) return {};
  
  return messages.reduce((acc, message) => {
    if (!message || !message.createdAt) return acc;
    
    const date = new Date(message.createdAt);
    const dateKey = format(date, 'yyyy-MM-dd');
    
    if (!acc[dateKey]) {
      acc[dateKey] = {
        date: date,
        dateFormatted: format(date, 'EEEE d MMMM yyyy', { locale: fr }),
        messages: []
      };
    }
    
    acc[dateKey].messages.push(message);
    return acc;
  }, {});
};

/**
 * Check if two messages should be grouped together
 * @param {Object} current - Current message
 * @param {Object} previous - Previous message in the list
 * @param {number} maxTimeDiff - Maximum time difference in minutes to group messages (default: 5)
 * @returns {boolean} True if messages should be grouped
 */
export const shouldGroupMessages = (current, previous, maxTimeDiff = 5) => {
  if (!current || !previous) return false;
  if (current.senderId !== previous.senderId) return false;
  
  const currentTime = new Date(current.createdAt).getTime();
  const previousTime = new Date(previous.createdAt).getTime();
  const diffInMinutes = (currentTime - previousTime) / (1000 * 60);
  
  return diffInMinutes <= maxTimeDiff;
};

/**
 * Generate a unique ID for a message
 * @param {Object} message - Message object
 * @returns {string} Unique ID
 */
export const generateMessageId = (message) => {
  if (!message) return '';
  
  // If message already has an ID, use it
  if (message.id) return message.id;
  
  // Generate a simple ID based on timestamp and random string
  const timestamp = message.createdAt 
    ? new Date(message.createdAt).getTime() 
    : Date.now();
  
  const randomStr = Math.random().toString(36).substring(2, 10);
  return `${timestamp}-${randomStr}`;
};

/**
 * Check if a message is from the current user
 * @param {Object} message - Message object
 * @param {string} currentUserId - Current user's ID
 * @returns {boolean} True if message is from current user
 */
export const isOwnMessage = (message, currentUserId) => {
  return message && message.senderId === currentUserId;
};

/**
 * Truncate a string to a specified length
 * @param {string} str - String to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated string with ellipsis if needed
 */
export const truncate = (str, maxLength = 30) => {
  if (!str) return '';
  return str.length > maxLength ? `${str.substring(0, maxLength)}...` : str;
};

/**
 * Format a message preview (for chat list)
 * @param {string} text - Message text
 * @param {number} maxLength - Maximum length of the preview
 * @returns {string} Formatted preview
 */
export const formatMessagePreview = (text, maxLength = 30) => {
  if (!text) return '';
  
  // Remove line breaks and extra spaces
  const cleanText = text.replace(/\s+/g, ' ').trim();
  return truncate(cleanText, maxLength);
};

/**
 * Get the unread message count for a conversation
 * @param {Object} conversation - Conversation object
 * @param {string} currentUserId - Current user's ID
 * @returns {number} Number of unread messages
 */
export const getUnreadCount = (conversation, currentUserId) => {
  if (!conversation || !conversation.messages || !currentUserId) return 0;
  
  return conversation.messages.reduce((count, message) => {
    return message.senderId !== currentUserId && !message.readAt ? count + 1 : count;
  }, 0);
};

/**
 * Sort conversations by most recent message
 * @param {Array} conversations - Array of conversation objects
 * @returns {Array} Sorted conversations
 */
export const sortConversationsByRecent = (conversations = []) => {
  return [...conversations].sort((a, b) => {
    const aDate = a.lastMessageAt || a.updatedAt || a.createdAt || 0;
    const bDate = b.lastMessageAt || b.updatedAt || b.createdAt || 0;
    
    // Convert to timestamps for comparison
    const aTime = new Date(aDate).getTime();
    const bTime = new Date(bDate).getTime();
    
    // Sort in descending order (newest first)
    return bTime - aTime;
  });
};
